import { useRouter } from 'next/router';
import React, { PropsWithChildren, RefObject } from 'react';
import { ModalProvider } from '@lib/modalContext';
import Meta from './Meta';
import Nav from './Nav';
import { Box } from '@lifechurch/react-ion';
import { createGlobalStyle } from 'styled-components';
import { AlertProvider } from '@lib/alertContext';
import { BeforeUnloadProvider } from '@lib/beforeUnloadContext';
import { useAuthContext } from '../lib/authContext';

const GlobalStyles = createGlobalStyle`
  html, body, #__next {
    height: 100%;
    width: 100%;
  }
  html, body {
    overflow: hidden;
  }
  #__next {
    overflow: auto;
  }
`;

interface PageProps {
  backgroundColor?: string;
  pageRef: RefObject<HTMLDivElement>;
}

// TODO: I'd prefer each page to render the title via <Head />, but  I kept getting a render error
// when doing so, so I extracted the logic to this component.
const getPageTitle = (organizationName: string, url: string): string => {
  let pageTitle = `Admin • ${organizationName} `;

  if (url === '/analytics/overview') {
    pageTitle += '- Analytics Overview';
  } else if (url.startsWith('/analytics/services')) {
    pageTitle += '- Analytics Services';
  } else if (url === '/analytics/reporting') {
    pageTitle += '- Analytics Reporting';
  } else if (url === '/services') {
    pageTitle += '- Services';
  } else if (url === '/service/new') {
    pageTitle += '- Add Service Time';
  } else if (url.endsWith('/content')) {
    pageTitle += '- Service Content';
  } else if (url.startsWith('/service')) {
    pageTitle += '- Edit Service Time';
  } else if (url === '/moments') {
    pageTitle += '- Moments';
  } else if (url === '/moment/add') {
    pageTitle += '- Add Moment';
  } else if (url.startsWith('/moment/')) {
    pageTitle += '- Edit Moment';
  } else if (url === '/users') {
    pageTitle += '- Users';
  } else if (url === '/user/new') {
    pageTitle += '- Invite User';
  } else if (url.startsWith('/user/')) {
    pageTitle += '- Edit User';
  } else if (url === '/integrations') {
    pageTitle += '- Integrations';
  } else if (url.startsWith('/integration/')) {
    pageTitle += '- Edit Integration';
  } else if (url === '/setup/appearance') {
    pageTitle += '- Setup Appearance';
  } else if (url === '/setup/offline') {
    pageTitle += '- Setup Offline';
  } else if (url === '/setup/sharing') {
    pageTitle += '- Setup Sharing';
  } else if (url === '/setup/domain') {
    pageTitle += '- Setup Domain';
  } else if (url === '/setup/info') {
    pageTitle += '- Setup Church Info';
  }
  return pageTitle.trim();
};

const Page: React.FC<PropsWithChildren<PageProps>> = ({
  backgroundColor = '#FFFFFF',
  children,
  pageRef,
}) => {
  const { currentOrganization, currentSubscriber } = useAuthContext();
  const router = useRouter();
  const pageTitle = getPageTitle(currentOrganization?.name ?? '', router.asPath);

  return (
    <>
      <Meta title={pageTitle} />
      <GlobalStyles />
      <AlertProvider>
        <ModalProvider>
          <BeforeUnloadProvider>
            <Box
              bg={backgroundColor}
              height='100vh'
              minWidth={1024}
              overflowX='auto'
              overflowY='hidden'
              position='relative'
            >
              <Box bg='gray5' height='100vh' overflowY='auto' position='absolute' width='220px'>
                <Nav currentSubscriber={currentSubscriber} />
              </Box>
              <Box height='100vh' overflowY='auto' ref={pageRef}>
                <Box
                  bg={backgroundColor}
                  data-testid='pageContent'
                  marginInlineStart='220px'
                  paddingBlockEnd={9}
                  paddingBlockStart={7}
                  px={7}
                  width='calc(100% - 220px)'
                >
                  <Box m='0 auto' maxWidth={1024}>
                    {children}
                  </Box>
                </Box>
              </Box>
            </Box>
          </BeforeUnloadProvider>
        </ModalProvider>
      </AlertProvider>
    </>
  );
};

export default Page;
